/**
 * Acqs file uploader — drag-and-drop multi-file queue, two-phase upload via
 * TmpFile.  Generic counterpart to acqs-image-uploader.js for AcqFile.
 *
 * Mounts on `[data-files-uploader]` cards (rendered by
 * `acqs/templates/acqs/_files_card.html`).  Each dropped file uploads
 * immediately to `[data-queue-add-url]`, which stores the bytes as a TmpFile
 * and returns the rendered queue row partial (`acqs/_file_queue_row.html`).
 * A skeleton placeholder is shown while the upload is in flight.  Save POSTs
 * JSON `{items: [{tmp_file_id, file_type, notes}, ...]}` to
 * `[data-upload-url]`, which promotes each TmpFile to an AcqFile with
 * metadata and returns the refreshed files card.
 *
 * Row markup (server-rendered) and file-type choices live in Django templates
 * and model constants — no HTML strings or duplicated choice lists in JS.
 *
 * FORM MODE (`[data-files-form-mode]`, used by the Create-Offer and Accept-Offer sheets): there
 * is no parent object yet and no upload URL.  Queue-add still stages a TmpFile,
 * but instead of an Upload button the row mirrors itself into hidden inputs
 * (`tmp_files`, `tmp_file_type_<id>`, `tmp_file_notes_<id>`) inside the row, so
 * the enclosing form carries the staged files on its own submit.  Removing the
 * row removes its inputs with it.  A POST from the same sheet is held while any
 * row is still uploading.  A card outside its form (the Create task sheet) has the
 * server render those inputs with `form=` (`hidden_inputs_form`); this script then only
 * updates their values.
 */

(function () {

    function getCsrfToken() {
        return window._cc && window._cc.getCsrfToken ? window._cc.getCsrfToken() : '';
    }

    function buildPlaceholder(filename) {
        // Skeleton row shown while the upload is in flight. Mirrors the
        // layout of `_file_queue_row.html`: file icon on the left, stacked
        // status rows on the right.  Replaced by the server-rendered partial
        // on success, or marked failed on error.
        var row = document.createElement('div');
        row.className = 'list-group-item';
        row.setAttribute('data-file-row', '');
        row.setAttribute('data-file-pending', '');
        row.title = filename;
        row.innerHTML = (
            '<div class="list-item-icon list-item-icon-muted">' +
                '<i class="fa-solid fa-spinner fa-spin"></i>' +
            '</div>' +
            '<div class="list-item-content d-flex flex-column gap-2">' +
                '<div class="text-muted fs-xs">' +
                    '<i class="fa-solid fa-spinner fa-spin"></i> Uploading…' +
                '</div>' +
                '<div class="text-truncate fs-xs text-faint" data-file-pending-name></div>' +
            '</div>'
        );
        row.querySelector('[data-file-pending-name]').textContent = filename;
        return row;
    }

    function markPlaceholderFailed(row, message) {
        row.removeAttribute('data-file-pending');
        row.setAttribute('data-file-failed', '');
        var status = row.querySelector('.text-muted');
        if (status) {
            status.classList.remove('text-muted');
            status.classList.add('text-error');
            status.innerHTML = '<i class="fa-solid fa-circle-exclamation"></i> ' + message;
        }
    }

    function init(card) {
        if (!card || card.hasAttribute('data-file-init')) return;
        card.setAttribute('data-file-init', '');

        var uploadUrl = card.getAttribute('data-upload-url');
        var formMode = card.hasAttribute('data-files-form-mode');
        var dropzone = card.querySelector('[data-file-dropzone]');
        var fileInput = card.querySelector('[data-file-input]');
        var queueEl = card.querySelector('[data-file-queue]');
        var saveBtn = card.querySelector('[data-file-save]');
        var cancelBtn = card.querySelector('[data-file-cancel]');
        var countEl = card.querySelector('[data-file-count]');

        if (!dropzone || !fileInput || !queueEl) return;
        if (!formMode && !saveBtn) return;

        function hiddenInput(row, name) {
            var el = row.querySelector('input[data-file-hidden="' + name + '"]');
            if (!el) {
                el = document.createElement('input');
                el.type = 'hidden';
                el.name = name;
                el.setAttribute('data-file-hidden', name);
                row.appendChild(el);
            }
            return el;
        }

        // Form mode: mirror the row's staged id + metadata into hidden inputs so
        // the enclosing form submits them.  They live INSIDE the row, so a
        // removed row takes its inputs with it.
        function syncRowInputs(row) {
            if (!formMode) return;
            var tmpId = row.getAttribute('data-tmp-file-id');
            if (!tmpId) return;
            var typeEl = row.querySelector('[data-file-type]');
            var notesEl = row.querySelector('[data-file-notes]');
            hiddenInput(row, 'tmp_files').value = tmpId;
            hiddenInput(row, 'tmp_file_type_' + tmpId).value = typeEl ? typeEl.value : '';
            hiddenInput(row, 'tmp_file_notes_' + tmpId).value = notesEl ? notesEl.value : '';
        }

        function readyRows() {
            return queueEl.querySelectorAll('[data-tmp-file-id]');
        }

        function pendingRows() {
            return queueEl.querySelectorAll('[data-file-pending]');
        }

        function refreshState() {
            var ready = readyRows().length;
            var pending = pendingRows().length;
            if (countEl) countEl.textContent = ready;
            if (saveBtn) saveBtn.disabled = ready === 0 || pending > 0;
        }

        function uploadFile(file) {
            var placeholder = buildPlaceholder(file.name);
            queueEl.appendChild(placeholder);
            refreshState();

            var formData = new FormData();
            formData.append('file', file);

            var xhr = new XMLHttpRequest();
            // Read per upload: the create sheet swaps it when the status changes.
            xhr.open('POST', card.getAttribute('data-queue-add-url'));
            xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
            xhr.onload = function () {
                if (xhr.status >= 200 && xhr.status < 300 && xhr.responseText.trim()) {
                    var tmp = document.createElement('div');
                    tmp.innerHTML = xhr.responseText.trim();
                    var newRow = tmp.firstElementChild;
                    if (newRow) {
                        placeholder.replaceWith(newRow);
                        syncRowInputs(newRow);
                    } else {
                        markPlaceholderFailed(placeholder, 'Upload failed');
                    }
                } else {
                    markPlaceholderFailed(placeholder, 'Upload failed');
                    if (window.SeeMe && SeeMe.toast) {
                        SeeMe.toast.error('Upload failed: ' + file.name);
                    }
                }
                refreshState();
            };
            xhr.onerror = function () {
                markPlaceholderFailed(placeholder, 'Upload failed');
                if (window.SeeMe && SeeMe.toast) {
                    SeeMe.toast.error('Upload failed: ' + file.name);
                }
                refreshState();
            };
            xhr.send(formData);
        }

        function addFiles(fileList) {
            for (var i = 0; i < fileList.length; i++) {
                uploadFile(fileList[i]);
            }
        }

        function removeRow(row) {
            var deleteUrl = row.getAttribute('data-tmp-file-delete-url');
            row.remove();
            refreshState();
            if (!deleteUrl) return;
            // Best-effort cleanup; the daily TmpFile sweeper would catch it anyway.
            var xhr = new XMLHttpRequest();
            xhr.open('DELETE', deleteUrl);
            xhr.setRequestHeader('X-CSRFToken', getCsrfToken());
            xhr.send();
        }

        function clearQueue() {
            // Remove each row individually so TmpFiles get DELETEd.
            var rows = queueEl.querySelectorAll('[data-file-row]');
            for (var i = 0; i < rows.length; i++) removeRow(rows[i]);
        }

        // Browse link + dropzone click → open file picker
        dropzone.addEventListener('click', function (e) {
            if (e.target === fileInput) return;
            e.preventDefault();
            fileInput.click();
        });

        fileInput.addEventListener('change', function () {
            addFiles(fileInput.files);
            fileInput.value = '';
        });

        // Drag-and-drop on dropzone
        var dragCounter = 0;
        dropzone.addEventListener('dragenter', function (e) {
            e.preventDefault();
            dragCounter++;
            dropzone.classList.add('file-uploader-dropzone-active');
        });
        dropzone.addEventListener('dragover', function (e) { e.preventDefault(); });
        dropzone.addEventListener('dragleave', function () {
            dragCounter--;
            if (dragCounter === 0) dropzone.classList.remove('file-uploader-dropzone-active');
        });
        dropzone.addEventListener('drop', function (e) {
            e.preventDefault();
            dragCounter = 0;
            dropzone.classList.remove('file-uploader-dropzone-active');
            addFiles(e.dataTransfer.files);
        });

        // Form mode: keep the hidden inputs in step with the row's controls.
        if (formMode) {
            var onRowEdit = function (e) {
                var row = e.target.closest('[data-file-row]');
                if (row) syncRowInputs(row);
            };
            queueEl.addEventListener('change', onRowEdit);
            queueEl.addEventListener('input', onRowEdit);
        }

        // Queue row remove
        queueEl.addEventListener('click', function (e) {
            var btn = e.target.closest('[data-file-remove]');
            if (!btn) return;
            var row = btn.closest('[data-file-row]');
            if (row) removeRow(row);
        });

        // Cancel — discard queue (the data-collapse-target attribute on the
        // button toggles the trigger/form visibility via SeeMe's global handler).
        if (cancelBtn) {
            cancelBtn.addEventListener('click', function () {
                clearQueue();
            });
        }

        // Save — POST JSON of staged TmpFiles + per-row metadata.  Form mode has
        // no Upload button and never posts here; its enclosing form is the only
        // upload action.
        if (saveBtn) saveBtn.addEventListener('click', function () {
            var rows = readyRows();
            if (rows.length === 0) return;

            saveBtn.disabled = true;

            var items = [];
            rows.forEach(function (row) {
                var typeEl = row.querySelector('[data-file-type]');
                var notesEl = row.querySelector('[data-file-notes]');
                items.push({
                    tmp_file_id: parseInt(row.getAttribute('data-tmp-file-id'), 10),
                    file_type: typeEl ? typeEl.value : 'other',
                    notes: notesEl ? notesEl.value : '',
                });
            });

            var uploadHeaders = {
                'X-CSRFToken': getCsrfToken(),
                'Content-Type': 'application/json',
                'HX-Current-URL': window.location.href,
            };
            // Plain fetch skips htmx:configRequest, so pass the tab id by hand or
            // this tab would see its own stale banner (acqs-ws.js).
            if (window.acqsTabId) uploadHeaders['X-Acqs-Tab-Id'] = window.acqsTabId;
            fetch(uploadUrl, {
                method: 'POST',
                body: JSON.stringify({items: items}),
                headers: uploadHeaders,
                credentials: 'same-origin',
            }).then(function (r) {
                if (!r.ok) throw new Error('Upload failed (' + r.status + ')');
                return r.text().then(function (html) {
                    return { html: html, headers: r.headers };
                });
            }).then(function (res) {
                var parent = card.parentNode;
                if (!parent) return;
                var tmp = document.createElement('div');
                tmp.innerHTML = res.html.trim();
                var newCard = tmp.firstElementChild;
                if (!newCard) {
                    saveBtn.disabled = false;
                    return;
                }
                card.replaceWith(newCard);
                if (window.htmx) htmx.process(newCard);
                // No htmx swap ran, so htmx:afterSettle never fires; clamp the new card's lists here.
                if (window._cc && window._cc.applyClampList) newCard.querySelectorAll('[data-clamp-list]').forEach(window._cc.applyClampList);

                // Swap any OOB fragments appended after the main card (e.g. count badges
                // in the outer tabbed wrapper that are outside the uploader's scope).
                var oobEls = tmp.querySelectorAll('[hx-swap-oob],[data-hx-swap-oob]');
                oobEls.forEach(function (el) {
                    var targetId = (el.getAttribute('hx-swap-oob') || el.getAttribute('data-hx-swap-oob') || '').replace(/^outerHTML:/, '');
                    var target = targetId ? document.querySelector(targetId) : null;
                    if (target) target.replaceWith(el);
                });

                // Bridge HX-Trigger toast (manual fetch — HTMX doesn't see this response)
                var trig = res.headers.get('HX-Trigger');
                if (trig && window.SeeMe && SeeMe.toast) {
                    try {
                        var data = JSON.parse(trig);
                        if (data.showToast && data.showToast.message) {
                            var fn = data.showToast.type === 'error' ? 'error' : 'success';
                            SeeMe.toast[fn](data.showToast.message);
                        }
                    } catch (e) { /* ignore malformed */ }
                }
            }).catch(function (err) {
                saveBtn.disabled = false;
                if (window.SeeMe && SeeMe.toast) SeeMe.toast.error(err.message || 'Upload failed');
            });
        });

        refreshState();
    }

    function scan(root) {
        var scope = root || document;
        if (scope.querySelectorAll) {
            scope.querySelectorAll('[data-files-uploader]').forEach(init);
        }
        if (scope.matches && scope.matches('[data-files-uploader]')) init(scope);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { scan(); });
    } else {
        scan();
    }
    document.body.addEventListener('htmx:afterSwap', function (e) {
        scan(e.detail.elt || document);
    });
    var observer = new MutationObserver(function () { scan(); });
    observer.observe(document.body, { childList: true, subtree: true });

    // Form mode: a row only gets its `tmp_files` input once its upload returns, so a
    // form posted mid-upload would go out without that file.  Hold the POST until the
    // sheet (or the form, outside a sheet) has no row still uploading.
    // `htmx:confirm`, not `beforeRequest`: it fires before loading.js disables the button,
    // so a held submit leaves the button usable.
    document.body.addEventListener('htmx:confirm', function (e) {
        var elt = e.detail.elt;
        if (!elt || !elt.closest || e.detail.verb === 'get') return;
        var scope = elt.closest('.sheet') || elt.closest('form');
        if (!scope || !scope.querySelector('[data-files-form-mode] [data-file-pending]')) return;
        e.preventDefault();
        if (window.SeeMe && SeeMe.toast) SeeMe.toast.error('Wait for the upload to finish');
    });

})();
